/**
 * Tab 5: Quick Actions
 * One-stop utility panel for manual administrative operations.
 */

import React, { useState } from 'react';
import {
  runDiagnostics,
  renewWebhookSubscriptions,
  triggerDeltaSync,
  reindexDocument,
} from '../../../api/adminApi';
import type { ActionResult } from '../../../types/admin';

interface ActionState {
  running: boolean;
  result: ActionResult | null;
}

function ActionCard({
  title,
  description,
  buttonLabel,
  onExecute,
  variant = 'primary',
  disabled = false,
  disabledReason,
  confirmMessage,
  children,
}: {
  title: string;
  description: string;
  buttonLabel: string;
  onExecute: () => Promise<ActionResult>;
  variant?: 'primary' | 'warning';
  disabled?: boolean;
  disabledReason?: string;
  confirmMessage?: string;
  children?: React.ReactNode;
}) {
  const [state, setState] = useState<ActionState>({ running: false, result: null });

  const handleClick = async () => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setState({ running: true, result: null });
    try {
      const result = await onExecute();
      setState({ running: false, result });
    } catch (err) {
      setState({
        running: false,
        result: {
          status: 'error',
          message: err instanceof Error ? err.message : 'Action failed',
        },
      });
    }
  };

  return (
    <div className="adm-card adm-action-card">
      <div className="adm-action-title">{title}</div>
      <p className="adm-action-desc">{description}</p>
      {children}
      <div className="adm-action-footer">
        <button
          className={`adm-btn ${variant === 'warning' ? 'adm-btn-warning' : ''}`}
          onClick={handleClick}
          disabled={state.running || disabled}
        >
          {state.running ? 'Running...' : disabled ? (disabledReason ?? buttonLabel) : buttonLabel}
        </button>
        {state.result && (
          <span
            className="adm-action-result"
            style={{
              color: state.result.status === 'success' ? '#28A745' : '#DC3545',
            }}
          >
            {state.result.status === 'success' ? '✓' : '✗'} {state.result.message}
          </span>
        )}
      </div>
    </div>
  );
}

export const QuickActions: React.FC = () => {
  const [reindexUrl, setReindexUrl] = useState('');

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-header">
        <h2 className="adm-tab-title">Quick Actions</h2>
      </div>

      <div className="adm-actions-grid">
        <ActionCard
          title="Run System Diagnostics"
          description="Run the golden query set and validate all services."
          buttonLabel="Run Diagnostics →"
          onExecute={runDiagnostics}
        />

        <ActionCard
          title="Force Webhook Renewal"
          description="Manually renew Graph webhook subscriptions before the 3-day expiry."
          buttonLabel="Renew Subscriptions →"
          onExecute={renewWebhookSubscriptions}
        />

        <ActionCard
          title="Trigger Delta Sync — DoyleIntranet"
          description="Pull latest changes from DoyleIntranet without waiting for a webhook event."
          buttonLabel="Sync DoyleIntranet →"
          onExecute={() => triggerDeltaSync('doyleintranet-kb')}
          confirmMessage="Trigger a delta sync for DoyleIntranet? This will process any changed files in _Published."
        />

        <ActionCard
          title="Trigger Delta Sync — RemoteProgramming"
          description="Pull latest changes from RemoteProgramming without waiting for a webhook event."
          buttonLabel="Sync RemoteProgramming →"
          onExecute={() => triggerDeltaSync('remoteprogramming-kb')}
          confirmMessage="Trigger a delta sync for RemoteProgramming? This will process any changed files in _Published."
        />

        <ActionCard
          title="Reindex Document"
          description="Re-process a specific document by SharePoint URL or file path."
          buttonLabel="Reindex →"
          onExecute={() => reindexDocument(reindexUrl)}
          disabled={!reindexUrl.trim()}
          disabledReason="Enter a URL first"
          confirmMessage={`Re-process this document? This will re-embed and update the index.\n\n${reindexUrl}`}
        >
          <input
            type="text"
            className="adm-input"
            placeholder="https://godoyle.sharepoint.com/sites/... or file path"
            value={reindexUrl}
            onChange={(e) => setReindexUrl(e.target.value)}
            style={{ marginTop: 8, marginBottom: 4 }}
          />
        </ActionCard>

        <ActionCard
          title="Flush Ephemeral Index"
          description="Remove all session-scoped documents older than N days."
          buttonLabel="Not available until F20 ships"
          onExecute={async () => ({ status: 'error' as const, message: 'F20 not yet active' })}
          disabled
          disabledReason="F20 — not yet active"
          variant="warning"
        />
      </div>
    </div>
  );
};
